import { useI18N } from "@src/language/language-context";
import { ActiveInstallationProcessPayload } from "@src/types/ota-update";
import { OTA_OBJECT_TYPE } from "@src/cloud-connect/ota-update/constants";

export const useOtaInstallationInProgressModal = (
  activeInstallationData: ActiveInstallationProcessPayload,
) => {
  const i18n = useI18N();

  const isBackupInstallation =
    activeInstallationData?.firmware?.type === OTA_OBJECT_TYPE.FIRMWARE_BACKUP;

  const detailsTemplate = isBackupInstallation
    ? i18n("modules.otaInstallationInProgressModal.installationDetailsBackup")
    : i18n(
        "modules.otaInstallationInProgressModal.installationDetailsFirmware",
      );

  const operationId =
    activeInstallationData?.update_details?.sftp_push_operation_id;
  const installationIdText = operationId
    ? i18n(
        "modules.otaInstallationInProgressModal.installationIdTemplate",
      ).replace("{ID}", String(operationId))
    : "";

  const firmwareName = activeInstallationData?.firmware?.filename || "";

  const installationDetails = detailsTemplate
    .replace("{itemName}", firmwareName)
    .replace("{installationId}", installationIdText);

  const itemNameMarker = "__ITEM_NAME__";
  const detailsTemplateWithMarker = detailsTemplate.replace(
    "{itemName}",
    itemNameMarker,
  );
  const [installationDetailsPrefixRaw, installationDetailsSuffixRaw = ""] =
    detailsTemplateWithMarker.split(itemNameMarker);

  const texts = {
    closeModalAriaLabel: isBackupInstallation
      ? i18n("modules.otaInstallationInProgressModal.closeModalAriaLabelBackup")
      : i18n(
          "modules.otaInstallationInProgressModal.closeModalAriaLabelFirmware",
        ),
    title: isBackupInstallation
      ? i18n("modules.otaInstallationInProgressModal.titleBackup")
      : i18n("modules.otaInstallationInProgressModal.titleFirmware"),
    installationDetails,
    installationDetailsHasItemName: detailsTemplate.includes("{itemName}"),
    installationDetailsPrefix: installationDetailsPrefixRaw.replace(
      "{installationId}",
      installationIdText,
    ),
    installationDetailsSuffix: installationDetailsSuffixRaw.replace(
      "{installationId}",
      installationIdText,
    ),
    firmwareName,
    description: isBackupInstallation
      ? i18n("modules.otaInstallationInProgressModal.descriptionBackup")
      : i18n("modules.otaInstallationInProgressModal.descriptionFirmware"),
    pushInBackgroundButtonText: i18n(
      "modules.otaInstallationInProgressModal.pushInBackgroundButtonText",
    ),
    firstStepText: i18n(
      "modules.otaInstallationInProgressModal.waitingBulletPointSecureConnection",
    ),
    secondStepText: i18n(
      "modules.otaInstallationInProgressModal.waitingBulletPointTransferFirmware",
    ),
    thirdStepText: i18n(
      "modules.otaInstallationInProgressModal.waitingBulletPointCreateBackup",
    ),
    fourthStepText: i18n(
      "modules.otaInstallationInProgressModal.waitingBulletPointUploadBackup",
    ),
    fifthStepText: i18n(
      "modules.otaInstallationInProgressModal.waitingBulletPointCopyFiles",
    ),
    targetPathFallback: i18n(
      "modules.otaInstallationInProgressModal.targetPathFallback",
    ),
  };

  const targetPath =
    activeInstallationData?.target_path || texts.targetPathFallback;

  const installationProgressSteps = [
    texts.firstStepText,
    texts.secondStepText,
    texts.thirdStepText.replace("{targetPath}", targetPath),
    texts.fourthStepText,
    texts.fifthStepText,
  ];

  return {
    texts,
    folders: activeInstallationData?.archive_structure_root_level || [],
    installationProgressSteps,
  };
};
